import React from 'react';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Text from '~/components/core/TextComponents/Text';
import type { PolicyListResponsePolicyListItemModel } from '~/server_shared/openapi/data-contracts';

interface PolicyDeleteDialogProps {
  open: boolean;
  policy?: PolicyListResponsePolicyListItemModel;
  onConfirm?: () => void;
  onCancel?: () => void;
  loading?: boolean;
}

const PolicyDeleteDialog: React.FC<PolicyDeleteDialogProps> = ({
  open,
  policy,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  if (!open || !policy) {
    return null;
  }

  return (
    <CardDialog
      open={open}
      onClose={onCancel}
      title="Delete Policy Confirmation"
      maxWidth="sm"
      fullWidth
      isDialog={true}
    >
      <Grid container direction="column" spacing={3}>
        {/* Warning Message */}
        <Grid item>
          <Text variant={Text.VARIANTS.SM} className="mb-4">
            Are you sure you want to delete the following policy?
          </Text>
        </Grid>

        {/* Policy Information */}
        <Grid item>
          <div className="bg-gray-50 mb-4 rounded border p-4">
            {policy.display_name && (
              <Text variant={Text.VARIANTS.SM} className="mb-2">
                <strong>Display Name:</strong> {policy.display_name}
              </Text>
            )}
            <Text variant={Text.VARIANTS.SM} className="mb-2">
              <strong>Policy Number:</strong> {policy.policy_number || 'N/A'}
            </Text>
            <Text variant={Text.VARIANTS.SM} className="mb-2">
              <strong>Policy UUID:</strong> {policy.policy_uuid}
            </Text>
            <Text variant={Text.VARIANTS.SM} className="mb-2">
              <strong>LOB:</strong> {policy.lob}
            </Text>
            <Text variant={Text.VARIANTS.SM}>
              <strong>Status:</strong> {policy.policy_status}
            </Text>
          </div>
        </Grid>

        {/* Destructive Action Warning */}
        <Grid item>
          <Text variant={Text.VARIANTS.SM} className="text-red-600 mb-4">
            ⚠️ This action cannot be undone.
          </Text>
        </Grid>

        {/* Action Buttons */}
        <Grid item>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <Button onClick={onCancel} color="secondary" disabled={loading}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={onConfirm} color="primary" variant="contained" disabled={loading}>
                {loading ? 'Deleting...' : 'Delete Policy'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </CardDialog>
  );
};

export default PolicyDeleteDialog;
